import React, { useMemo } from 'react'
import MenuItem from '@mui/material/MenuItem'
import Grid from '@mui/material/Grid'
import Select, { type SelectChangeEvent } from '@mui/material/Select'
import { type FilterStatusMap, type UserFilters } from '@utils/StatusMapper'
import useTestId from '@utils/useTestId'
import { FormControl, InputLabel } from '@mui/material'
import { UsState } from '@generatedTypes/graphql'
import FirmsDropdown from '@components/organisms/FirmsDropDown'

interface FilterValues {
  value: string
  label: string
}

type FilterOptions = Array<'status' | 'state' | 'firms'>

interface LegalMatterFilterProps {
  selectedStatus: string
  selectedFirmRef?: string | null
  statusList: FilterStatusMap
  usStates: UsState[] | undefined
  preferredStates: string[]
  disableFilters?: FilterOptions
  onChange: (value: UserFilters) => void
  'data-testid'?: string
}

const LegalMatterFilters: React.FC<LegalMatterFilterProps> = ({
  selectedStatus,
  selectedFirmRef,
  statusList,
  usStates,
  preferredStates,
  disableFilters = [],
  onChange,
  ...props
}) => {
  const testId = useTestId(props)
  const handleStatusChange = (event: SelectChangeEvent): void => {
    const value = event.target.value
    onChange({ status: value })
  }

  const handleFirmChange = (firmRef: string | null): void => {
    onChange({ firmRef: firmRef })
  }

  const handleUsStateChange = (e: any) => {
    const raw = e.target.value as string[]
    const next = raw.includes('') ? [] : raw
    onChange({ usStates: next })
  }

  const mapNamesAndValues = (object: FilterStatusMap): FilterValues[] => {
    return Object.entries(object).map(([key, value]) => ({
      value: key,
      label: value.label,
    }))
  }

  const stateCodeToName = useMemo(() => {
    const map = new Map<string, string>()
    usStates?.forEach(({ code, name }) => map.set(code, name))
    return map
  }, [usStates])

  const renderStateValue = (selected: unknown) => {
    const list = selected as string[]
    if (!list.length) return <em>All States</em>

    return list.map((code) => stateCodeToName.get(code) ?? code).join(', ')
  }

  const mappedValues = mapNamesAndValues(statusList)

  return (
    <Grid container justifyContent="space-between" alignItems="center">
      <Grid item mb={2}>
        <Grid
          container
          spacing={2}
          alignItems="center"
          data-testid="legal-matter-filters"
        >
          <Grid item xs="auto">
            <FormControl sx={{ minWidth: 220 }} variant="outlined">
              <InputLabel id="status-label">Filter by Status</InputLabel>
              <Select
                {...testId}
                labelId="status-label"
                value={selectedStatus}
                onChange={handleStatusChange}
                label="Filter by Status"
                data-testid="status-filter"
              >
                {mappedValues.map(({ value, label }) => (
                  <MenuItem key={value} value={value}>
                    {label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs="auto">
            <FormControl sx={{ minWidth: 220 }} variant="outlined">
              <InputLabel shrink id="state-label">
                Filter by State
              </InputLabel>
              <Select
                multiple
                displayEmpty
                labelId="state-label"
                value={preferredStates}
                onChange={handleUsStateChange}
                renderValue={renderStateValue}
                label="Filter by State"
                data-testid="state-filter"
              >
                {usStates?.map(({ code, name }) => (
                  <MenuItem key={code} value={code}>
                    {name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Grid>
      {!disableFilters.includes('firms') && (
        <Grid item mb={2}>
          <FirmsDropdown
            onChange={handleFirmChange}
            firmRef={selectedFirmRef}
          />
        </Grid>
      )}
    </Grid>
  )
}

export default LegalMatterFilters
